'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircleIcon, CheckCircleIcon } from '@/components/icons';
import { cn } from '@/lib/utils';

const inputVariants = cva(
  [
    // Base
    'peer w-full bg-transparent',
    'text-[var(--text-primary)] placeholder-transparent',
    // Border
    'border border-[var(--border-default)] rounded-card',
    // Transitions
    'transition-all duration-200',
    // Focus
    'focus:outline-none focus:border-[var(--primary)]',
    // Disabled
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--surface-disabled)]',
    // File
    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
  ],
  {
    variants: {
      // text-[16px] здесь НЕ переводится на роль типографики и не уменьшается.
      // Это не про дизайн: Safari на iOS автоматически зумит страницу при
      // фокусе в поле с кеглем меньше 16px. Любая роль из шкалы (14 или 15)
      // вернёт этот зум на всех формах, включая оформление заказа.
      size: {
        sm: 'h-9 px-3 pt-4 pb-1 text-[16px] leading-tight',
        default: 'h-12 px-4 pt-5 pb-2 text-[16px]',
        lg: 'h-14 px-4 pt-6 pb-2 text-base',
      },
      hasError: {
        true: [
          'border-[var(--error)] bg-[var(--error)]/5',
          'focus:border-[var(--error)]',
        ],
        false: '',
      },
      hasSuccess: {
        true: [
          'border-[var(--lime-500)] bg-[var(--lime-500)]/5',
          'focus:border-[var(--lime-500)]',
        ],
        false: '',
      },
    },
    defaultVariants: {
      size: 'lg',
      hasError: false,
      hasSuccess: false,
    },
  }
);

const labelVariants = cva(
  [
    'absolute left-4 text-[var(--text-tertiary)] pointer-events-none',
    'transition-all duration-200 ease-out',
    // Floating animation
    'peer-focus:text-xs peer-focus:top-2 peer-focus:text-[var(--primary)]',
    'peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:top-2',
  ],
  {
    variants: {
      size: {
        sm: 'text-xs top-2.5 peer-focus:top-1 peer-[:not(:placeholder-shown)]:top-1',
        default: 'text-sm top-3.5 peer-focus:top-2 peer-[:not(:placeholder-shown)]:top-2',
        lg: 'text-base top-4 peer-focus:top-2 peer-[:not(:placeholder-shown)]:top-2',
      },
      hasError: {
        true: 'text-[var(--error)] peer-focus:text-[var(--error)] peer-[:not(:placeholder-shown)]:text-[var(--error)]',
        false: '',
      },
      hasSuccess: {
        true: 'text-[var(--success)] peer-focus:text-[var(--success)] peer-[:not(:placeholder-shown)]:text-[var(--success)]',
        false: '',
      },
    },
    defaultVariants: {
      size: 'lg',
      hasError: false,
      hasSuccess: false,
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      label,
      error,
      success,
      hint,
      size = 'lg',
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const hasError = !!error;
    const hasSuccess = !!success && !hasError;

    return (
      <div className="w-full space-y-2">
        <div className="relative w-full">
          {/* Input */}
          <input
            id={inputId}
            type={type}
            className={cn(
              inputVariants({ size, hasError, hasSuccess }),
              (hasError || hasSuccess) && 'pr-11',
              className
            )}
            placeholder=" "
            disabled={disabled}
            ref={ref}
            aria-invalid={hasError}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />

          {/* Floating Label */}
          {label && (
            <label
              htmlFor={inputId}
              className={labelVariants({ size, hasError, hasSuccess })}
            >
              {label}
            </label>
          )}

          {/* Status Icon */}
          {hasError && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <AlertCircleIcon className="h-5 w-5 text-[var(--error)]" weight="fill" />
            </div>
          )}
          {hasSuccess && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <CheckCircleIcon className="h-5 w-5 text-[var(--success)]" weight="fill" />
            </div>
          )}
        </div>

        {/* Helper text */}
        {error && (
          <div className="flex items-center gap-1.5 pl-1">
            <p id={`${inputId}-error`} className="text-xs font-medium text-[var(--error)]">
              {error}
            </p>
          </div>
        )}
        {success && !error && (
          <div className="flex items-center gap-1.5 pl-1">
            <p className="text-xs font-medium text-[var(--success)]">
              {success}
            </p>
          </div>
        )}
        {hint && !error && !success && (
          <p id={`${inputId}-hint`} className="text-xs text-[var(--text-tertiary)] pl-1">
            {hint}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
